/**
 * Header notification bell for the ScrapeJob system.
 *
 * Renders a circular bell trigger with an unread badge. Click opens a
 * popover with three sections: needs-attention (2FA), in-progress, and
 * recently-finished. Each entry is a card with a colored side-rail, a
 * status icon in a tinted circle, and a clean two-line layout — patient
 * name on top, carrier · operation below. Clicking a card navigates to
 * that patient's treatment plan.
 *
 * "Seen" state is per-device via localStorage and used to drive the
 * unread-dot decoration on each row plus the badge count.
 *
 * react-redux 6.x — uses connect() (no hooks).
 */

import React from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { Popover, Tooltip, message } from "antd";
import {
  BellOutlined,
  CheckOutlined,
  CloseOutlined,
  KeyOutlined,
  LoadingOutlined,
  StopOutlined,
} from "@ant-design/icons";

import { apiService } from "../../_services/api.service";
import { debugLog } from "../../utils/debugLog";
import { loadViewerRole } from "../viewerRole";
import {
  selectActiveJobs,
  selectAwaitingTwoFAJobs,
  selectRecentCompletedJobs,
  SCRAPE_JOB_UPSERT,
} from "../../reducers/scrapeJobs";
import "./ScrapeJobsIndicator.css";

const SEEN_KEY = "scrapeJobsSeen";

function loadSeen() {
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (e) {
    return {};
  }
}

function persistSeen(map) {
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify(map));
  } catch (e) {
    /* noop */
  }
}

function jobLabel(job) {
  const op = (job.operation_type || "").replace(/_/g, " ");
  if (!op) return "Insurance fetch";
  return op.charAt(0).toUpperCase() + op.slice(1);
}

function carrierName(job) {
  if (!job || !job.carrier_code) return "";
  const c = String(job.carrier_code).toLowerCase();
  return c.charAt(0).toUpperCase() + c.slice(1);
}

function patientLabel(job) {
  return job.patient_name || (job.patient_id ? "Patient #" + job.patient_id : "Patient");
}

function formatRelative(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diffSec = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  if (diffSec < 5) return "just now";
  if (diffSec < 60) return diffSec + "s ago";
  if (diffSec < 3600) return Math.floor(diffSec / 60) + "m ago";
  if (diffSec < 86400) return Math.floor(diffSec / 3600) + "h ago";
  return Math.floor(diffSec / 86400) + "d ago";
}

function activeStatusLine(job) {
  const carrier = carrierName(job);
  const op = jobLabel(job);
  switch (job.state) {
    case "queued":  return "Waiting in queue · " + op + (carrier ? " · " + carrier : "");
    case "claimed": return "Starting · " + op + (carrier ? " · " + carrier : "");
    case "running": return "Running · " + op + (carrier ? " · " + carrier : "");
    default:        return op + (carrier ? " · " + carrier : "");
  }
}

function terminalStatusLine(job) {
  const carrier = carrierName(job);
  const op = jobLabel(job);
  return op + (carrier ? " · " + carrier : "");
}

function variantForActive(job) {
  return job.state === "awaiting_2fa" ? "2fa" : "running";
}

function variantForTerminal(job) {
  if (job.state === "succeeded") return "success";
  if (job.state === "failed" || job.state === "timed_out") return "failed";
  return "cancelled";
}

function iconForTerminal(job) {
  if (job.state === "succeeded") return <CheckOutlined />;
  if (job.state === "failed" || job.state === "timed_out") return <CloseOutlined />;
  return <StopOutlined />;
}

class ScrapeJobsNotificationButton extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      open: false,
      seen: loadSeen(),
      // Who is looking (viewerRole). Until it answers the viewer is office staff.
      viewer: null,
    };
  }

  componentDidMount() {
    this.alive = true;
    loadViewerRole().then((viewer) => {
      if (this.alive) this.setState({ viewer });
    });
  }

  componentWillUnmount() {
    this.alive = false;
  }

  // The finished runs this viewer is told about. Office staff see the
  // successes only: a failed run is ours to act on, and a red row with an
  // unread dot would be the very notification the agent must not send them.
  visibleCompleted() {
    const { viewer } = this.state;
    const all = this.props.recentCompleted || [];
    return viewer && viewer.isFtc ? all : all.filter((j) => j.state === "succeeded");
  }

  computeUnreadCount() {
    const seen = this.state.seen;
    let n = 0;
    this.props.awaiting2faJobs.forEach((j) => { if (!seen[j.job_id]) n += 1; });
    this.visibleCompleted().forEach((j) => { if (!seen[j.job_id]) n += 1; });
    return n;
  }

  markAllVisibleSeen = () => {
    const stamp = new Date().toISOString();
    const next = { ...this.state.seen };
    this.props.awaiting2faJobs.forEach((j) => { next[j.job_id] = stamp; });
    this.visibleCompleted().forEach((j) => { next[j.job_id] = stamp; });
    this.setState({ seen: next });
    persistSeen(next);
  };

  handleOpenChange = (visible) => {
    this.setState({ open: visible });
    if (visible) {
      debugLog.info("bell", "notif_open", { unread_count: this.computeUnreadCount() });
      // Defer the seen-mark so users get a chance to see the unread treatment
      // (subtle dot on each row) before it fades.
      setTimeout(this.markAllVisibleSeen, 250);
    }
  };

  navigateToPatient = (job) => {
    if (!job || !job.patient_id) return;
    this.setState({ open: false });
    if (this.props.history) {
      this.props.history.push("/treatment-plans/" + job.patient_id);
    }
  };

  handleCancel = async (job) => {
    try {
      const data = await apiService.post(
        "/clinics/api/scrape-jobs/" + job.job_id + "/cancel/",
        {}
      );
      if (data && data.job_id) this.props.upsertJob(data);
      message.success("Cancelled");
    } catch (e) {
      message.error("Could not cancel");
    }
  };

  openTwoFA = (job) => {
    this.setState({ open: false });
    window.dispatchEvent(
      new CustomEvent("scrapeJobs.openTwoFA", { detail: { jobId: job.job_id } })
    );
  };

  renderActiveRow(job) {
    const variant = variantForActive(job);
    const className = "scrape-popover-row scrape-popover-row-" + variant;
    const isCancellable = job.state === "queued" || job.state === "awaiting_2fa";
    return (
      <div
        key={job.job_id}
        className={className}
        onClick={() => this.navigateToPatient(job)}
        role="button"
        tabIndex={0}
      >
        <div className="scrape-popover-row-icon">
          <LoadingOutlined spin />
        </div>
        <div className="scrape-popover-row-body">
          <div className="scrape-popover-row-name">{patientLabel(job)}</div>
          <div className="scrape-popover-row-meta">{activeStatusLine(job)}</div>
        </div>
        <div className="scrape-popover-row-aside">
          <span className="scrape-popover-row-time">{formatRelative(job.created_at)}</span>
          {isCancellable ? (
            <Tooltip title="Cancel this fetch">
              <button
                type="button"
                className="scrape-popover-cancel-btn"
                onClick={(e) => { e.stopPropagation(); this.handleCancel(job); }}
                aria-label="Cancel"
              >
                <CloseOutlined />
              </button>
            </Tooltip>
          ) : null}
        </div>
      </div>
    );
  }

  renderTwoFARow(job) {
    return (
      <div
        key={job.job_id}
        className="scrape-popover-row scrape-popover-row-2fa"
      >
        <div className="scrape-popover-row-icon"><KeyOutlined /></div>
        <div className="scrape-popover-row-body">
          <div className="scrape-popover-row-name">{patientLabel(job)}</div>
          <div className="scrape-popover-row-meta">
            Waiting for 2FA code · {carrierName(job) || "carrier"} · {jobLabel(job)}
          </div>
        </div>
        <div className="scrape-popover-row-aside">
          <button
            type="button"
            className="scrape-popover-2fa-btn"
            onClick={() => this.openTwoFA(job)}
          >
            Submit code
          </button>
        </div>
      </div>
    );
  }

  renderCompletedRow(job) {
    const variant = variantForTerminal(job);
    const className = "scrape-popover-row scrape-popover-row-" + variant;
    const seen = this.state.seen;
    const isUnread = !seen[job.job_id];
    return (
      <div
        key={job.job_id}
        className={className}
        onClick={() => this.navigateToPatient(job)}
        role="button"
        tabIndex={0}
      >
        <div className="scrape-popover-row-icon">{iconForTerminal(job)}</div>
        <div className="scrape-popover-row-body">
          <div className="scrape-popover-row-name">{patientLabel(job)}</div>
          <div className="scrape-popover-row-meta">{terminalStatusLine(job)}</div>
          {job.error_message && variant === "failed" ? (
            <span className="scrape-popover-row-error">{job.error_message}</span>
          ) : null}
        </div>
        <div className="scrape-popover-row-aside">
          {isUnread ? <span className="scrape-popover-row-unread-dot" aria-label="Unread" /> : null}
          <span className="scrape-popover-row-time">
            {formatRelative(job.completed_at || job.updated_at)}
          </span>
        </div>
      </div>
    );
  }

  render() {
    const { activeJobs, awaiting2faJobs } = this.props;
    const recentCompleted = this.visibleCompleted();
    const unreadCount = this.computeUnreadCount();
    const hasAttention = awaiting2faJobs.length > 0;
    // 2FA jobs already get their own urgent section above; don't repeat
    // them in the in-progress list.
    const inProgressJobs = activeJobs.filter(function (j) {
      return j.state !== "awaiting_2fa";
    });

    const popoverContent = (
      <div className="scrape-popover">
        {hasAttention ? (
          <div className="scrape-popover-section">
            <div className="scrape-popover-section-header scrape-popover-section-header-2fa">
              <span>Needs your attention</span>
              <span className="scrape-popover-section-count">{awaiting2faJobs.length}</span>
            </div>
            {awaiting2faJobs.map((j) => this.renderTwoFARow(j))}
          </div>
        ) : null}

        <div className="scrape-popover-section">
          <div className="scrape-popover-section-header">
            <span>In progress</span>
            <span className="scrape-popover-section-count">{inProgressJobs.length}</span>
          </div>
          {inProgressJobs.length === 0 ? (
            <div className="scrape-popover-empty">Nothing running right now.</div>
          ) : (
            inProgressJobs.map((j) => this.renderActiveRow(j))
          )}
        </div>

        <div className="scrape-popover-section">
          <div className="scrape-popover-section-header">
            <span>Recently finished</span>
            {recentCompleted.length > 0 ? (
              <span className="scrape-popover-section-count">{recentCompleted.length}</span>
            ) : null}
          </div>
          {recentCompleted.length === 0 ? (
            <div className="scrape-popover-empty">No recent activity.</div>
          ) : (
            recentCompleted.map((j) => this.renderCompletedRow(j))
          )}
        </div>
      </div>
    );

    const triggerClassName =
      "scrape-bell-trigger" + (this.state.open ? " is-open" : "");
    const badgeClassName =
      "scrape-bell-trigger-badge" +
      (hasAttention ? " scrape-bell-trigger-badge-attention" : "");
    const badgeLabel = unreadCount > 99 ? "99+" : String(unreadCount);

    return (
      <Popover
        content={popoverContent}
        title="Insurance fetches"
        trigger="click"
        open={this.state.open}
        onOpenChange={this.handleOpenChange}
        placement="bottomRight"
        overlayStyle={{ paddingTop: 4 }}
      >
        <button
          type="button"
          className={triggerClassName}
          aria-label={`Insurance fetch notifications${unreadCount ? ` (${unreadCount} unread)` : ""}`}
          aria-haspopup="dialog"
          aria-expanded={this.state.open}
        >
          <BellOutlined />
          {unreadCount > 0 ? (
            <span className={badgeClassName}>{badgeLabel}</span>
          ) : null}
        </button>
      </Popover>
    );
  }
}

const mapStateToProps = (state) => ({
  activeJobs: selectActiveJobs(state),
  awaiting2faJobs: selectAwaitingTwoFAJobs(state),
  recentCompleted: selectRecentCompletedJobs(state, 20),
});

const mapDispatchToProps = (dispatch) => ({
  upsertJob: (job) => dispatch({ type: SCRAPE_JOB_UPSERT, job }),
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(ScrapeJobsNotificationButton)
);
